import { styled, Text } from '@obvio/app'
import { SvgBin, SvgPlus } from '@obvio/svg'
import {
  AggregateData,
  Button,
  HiddenInput,
  Stack,
  TextInput,
  useFormContext,
} from '@obvio/ui'
import { Label } from '@obvio/ui/Form/Inputs/utils'
import { cuid } from '@obvio/utils'
import { useState } from 'react'

import type { Dict } from '@obvio/utils'
import type { ReactElement } from 'react'

const StandaloneLabel = styled(Label)`
  position: static;
  text-align: left;
  margin-top: ${(theme) => theme.spacing.large} !important;
  ${(theme) => theme.inputs.textarea?.label};
`

const AddMoreButton = styled(Button)`
  margin-top: ${(theme) => theme.spacing.medium};
`

type FormMultipleProps = {
  label: string
  name: string
  optional?: boolean
  defaultNum?: number
}

function aggregateMultiple(data: Dict<string>) {
  return Object.entries(data ?? {}).filter((v) => Boolean(v[1]))
}

export function FormMultiple({
  label,
  name,
  optional = false,
  defaultNum = 0,
}: FormMultipleProps): ReactElement {
  const { removeInput, getInputs } = useFormContext()
  const [inputs, setInputs] = useState<[string, string][]>(
    getInputs()?.[name] ??
      Array.from({ length: defaultNum }, () => [cuid(), '']),
  )

  const addInput = () => {
    setInputs((i) => [...i, [cuid(), '']])
  }

  const removeInputHandler = (idx: number) => {
    removeInput(`${label}-${inputs[idx][0]}`)
    const copy = [...inputs]
    copy.splice(idx, 1)
    setInputs(copy)
  }

  return (
    <AggregateData name={name} aggregate={aggregateMultiple}>
      <HiddenInput name={label} optional />
      <Stack kind="vertical" align="start" spacing="large">
        <StandaloneLabel optional={optional} inputKind="text">
          {label}
        </StandaloneLabel>
        {
          inputs.map((input, idx) => (
            <TextInput
              key={input[0]}
              name={input[0]}
              defaultValue={input[1]}
              label="Year and Title"
              suffix={
                <Button
                  kind={['icon', 'transparent']}
                  onClick={() => removeInputHandler(idx)}
                >
                  <SvgBin />
                </Button>
              }
            />
          )) as unknown as ReactElement
        }
        <AddMoreButton onClick={addInput} kind="ghost">
          <Stack>
            <SvgPlus />
            <Text tag="span">ADD MORE</Text>
          </Stack>
        </AddMoreButton>
      </Stack>
    </AggregateData>
  )
}
